import type { BillingInterval, CheckoutTier } from "../types"

type ClerkInviteStatus = "sign_in" | "sign_up" | "complete"

export function readTierQuery(value: unknown): CheckoutTier | null {
  if (typeof value !== "string") {
    return null
  }
  return value === "starter" || value === "growth" ? value : null
}

/**
 * `source` query value the marketing pricing page appends to its signup links
 * (`/signup?source=pricing`). Shared across routing and tests so the token
 * stays a single contract.
 */
const PRICING_SOURCE = "pricing" as const

/**
 * True when signup was reached from the marketing pricing page, which links to
 * `/signup?source=pricing`. Used to surface the free trial in the headline.
 */
export function isPricingSource(value: unknown): boolean {
  return value === PRICING_SOURCE
}

export function readBillingQuery(value: unknown): BillingInterval | null {
  if (typeof value !== "string") {
    return null
  }
  if (value === "annual" || value === "year") {
    return "year"
  }
  if (value === "monthly" || value === "month") {
    return "month"
  }
  return null
}

export function buildWelcomeUrl(
  tier: CheckoutTier | null,
  billing: BillingInterval | null,
): string {
  const params = new URLSearchParams()
  if (tier) {
    params.set("tier", tier)
  }
  if (billing === "year") {
    params.set("billing", "annual")
  }
  const qs = params.toString()
  return qs ? `/signup-welcome?${qs}` : "/signup-welcome"
}

export function buildSignInHref(
  tier: CheckoutTier | null,
  billing: BillingInterval | null,
): string {
  const redirect = buildWelcomeUrl(tier, billing)
  return `/signin?redirect_url=${encodeURIComponent(redirect)}`
}

export function readClerkTicketQuery(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }
  const ticket = value.trim()
  return ticket.length > 0 ? ticket : null
}

export function readClerkInviteStatusQuery(
  value: unknown,
): ClerkInviteStatus | null {
  if (value === "sign_in" || value === "sign_up" || value === "complete") {
    return value
  }
  return null
}

export function buildClerkInviteHref(
  path: "/signin" | "/signup",
  ticket: string,
  status: ClerkInviteStatus,
): string {
  const params = new URLSearchParams({
    __clerk_ticket: ticket,
    __clerk_status: status,
  })
  return `${path}?${params.toString()}`
}
